import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { updatePartnerOffer } from '@/lib/firebase/queries/offers';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { PartnerOffer } from './types';

interface EditOfferDialogProps {
  offer: PartnerOffer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditOfferDialog({ offer, open, onOpenChange, onSuccess }: EditOfferDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pointsRequired, setPointsRequired] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [terms, setTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (offer) {
      setTitle(offer.title);
      setDescription(offer.description);
      setPointsRequired(offer.pointsRequired.toString());
      setValidUntil(offer.validUntil.toISOString().split('T')[0]);
      setTerms(offer.terms);
    }
  }, [offer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!offer || !title || !description || !pointsRequired || !validUntil || !terms) {
      toast.error(t('dashboard.partner.dashboard.offers.form.error'));
      return;
    }

    try {
      setSaving(true);
      await updatePartnerOffer(offer.id, {
        title,
        description,
        pointsRequired: parseInt(pointsRequired),
        validUntil: new Date(validUntil),
        terms
      });

      toast.success(t('dashboard.partner.dashboard.offers.form.success'));
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error(t('dashboard.partner.dashboard.offers.form.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" dir={dir}>
        <DialogHeader>
          <DialogTitle>{t('dashboard.partner.dashboard.offers.table.edit')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('dashboard.partner.dashboard.offers.form.title')}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('dashboard.partner.dashboard.offers.form.description')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="points">{t('dashboard.partner.dashboard.offers.form.points')}</Label>
            <Input
              id="points"
              type="number"
              min="0"
              value={pointsRequired}
              onChange={(e) => setPointsRequired(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="validUntil">{t('dashboard.partner.dashboard.offers.form.validUntil')}</Label>
            <Input
              id="validUntil"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="terms">{t('dashboard.partner.dashboard.offers.form.terms')}</Label>
            <Textarea
              id="terms"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('dashboard.partner.dashboard.offers.form.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {t('dashboard.partner.dashboard.offers.form.saving')}
                </>
              ) : (
                t('dashboard.partner.dashboard.offers.form.save')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 